// ─── Un lector de XML pequeño, sin dependencias ─────────────────────────────
// Para leer la factura electrónica del proveedor (UBL 2.1 de la DIAN). Corre
// igual en el navegador y en las pruebas (Node no trae DOMParser), y no
// necesita más: etiquetas, atributos, texto, CDATA y entidades. Los prefijos
// de espacio de nombres se ignoran al buscar (`cbc:ID` se busca como `ID`),
// porque cada facturador los declara a su manera.
//
// Nodo: { name (sin prefijo), qname, attrs, children, text }.

const ENTIDADES = { lt: "<", gt: ">", amp: "&", quot: '"', apos: "'" };
function decodificar(s) {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e) => {
    if (e[0] === "#") {
      const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : m;
    }
    return ENTIDADES[e.toLowerCase()] ?? m;
  });
}
const local = (q) => { const i = q.indexOf(":"); return i >= 0 ? q.slice(i + 1) : q; };

export function parseXML(src) {
  if (typeof src !== "string") throw new Error("El XML llegó vacío.");
  const raiz = { name: "#documento", qname: "#documento", attrs: {}, children: [], text: "" };
  const pila = [raiz];
  const tope = () => pila[pila.length - 1];
  let i = 0;
  while (i < src.length) {
    const lt = src.indexOf("<", i);
    if (lt < 0) { tope().text += decodificar(src.slice(i)); break; }
    if (lt > i) tope().text += decodificar(src.slice(i, lt));
    if (src.startsWith("<!--", lt)) {
      const fin = src.indexOf("-->", lt);
      if (fin < 0) throw new Error("XML mal formado: un comentario sin cerrar.");
      i = fin + 3; continue;
    }
    if (src.startsWith("<![CDATA[", lt)) {
      const fin = src.indexOf("]]>", lt);
      if (fin < 0) throw new Error("XML mal formado: un CDATA sin cerrar.");
      tope().text += src.slice(lt + 9, fin);
      i = fin + 3; continue;
    }
    if (src.startsWith("<?", lt) || src.startsWith("<!", lt)) {
      const fin = src.indexOf(">", lt);
      if (fin < 0) throw new Error("XML mal formado.");
      i = fin + 1; continue;
    }
    if (src[lt + 1] === "/") {
      const fin = src.indexOf(">", lt);
      if (fin < 0) throw new Error("XML mal formado: una etiqueta de cierre sin terminar.");
      if (pila.length > 1) pila.pop();
      i = fin + 1; continue;
    }
    // Etiqueta de apertura: el ">" que la cierra no puede estar entre comillas.
    let j = lt + 1, comilla = null;
    while (j < src.length) {
      const c = src[j];
      if (comilla) { if (c === comilla) comilla = null; }
      else if (c === '"' || c === "'") comilla = c;
      else if (c === ">") break;
      j++;
    }
    if (j >= src.length) throw new Error("XML mal formado: una etiqueta sin cerrar.");
    let cuerpo = src.slice(lt + 1, j);
    const autoCierre = cuerpo.endsWith("/");
    if (autoCierre) cuerpo = cuerpo.slice(0, -1);
    const qname = (cuerpo.match(/^[^\s/>]+/) || [""])[0];
    const attrs = {};
    const re = /([^\s=]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
    let m;
    while ((m = re.exec(cuerpo.slice(qname.length)))) attrs[local(m[1])] = decodificar(m[3] ?? m[4] ?? "");
    const nodo = { name: local(qname), qname, attrs, children: [], text: "" };
    tope().children.push(nodo);
    if (!autoCierre) pila.push(nodo);
    i = j + 1;
  }
  return raiz;
}

// El primer hijo que siga la ruta de nombres (sin prefijo).
export function hijo(nodo, ...ruta) {
  let n = nodo;
  for (const nombre of ruta) {
    if (!n) return null;
    n = n.children.find(c => c.name === nombre) || null;
  }
  return n;
}
export const hijos = (nodo, nombre) => (nodo ? nodo.children.filter(c => c.name === nombre) : []);
export const texto = (nodo, ...ruta) => { const n = ruta.length ? hijo(nodo, ...ruta) : nodo; return n ? n.text.trim() : ""; };

// Todos los descendientes con ese nombre, en orden.
export function buscarTodos(nodo, nombre, salida = []) {
  for (const c of (nodo ? nodo.children : [])) {
    if (c.name === nombre) salida.push(c);
    buscarTodos(c, nombre, salida);
  }
  return salida;
}
